import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  type OutletResource,
  OutletsController_create_v1,
  type OutletsController_create_v1Body,
  type TaxIdentityResource,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { Switch } from '@repo/ui/components/switch';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { outletCodeSchema } from '@/lib/field-schemas';
import { IDEMPOTENCY_KEY_HEADER, newIdempotencyKey } from '@/lib/idempotency-key';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';
import {
  hintForKind,
  labelForKind,
  ordinarilyCarriesStock,
  OUTLET_KIND_ITEMS,
  OUTLET_KINDS,
  type OutletKind,
} from './outlet-kinds';
import { TAX_IDENTITY_REQUIRED } from './registrations';
import { RegistrationSelect } from './RegistrationSelect';

/**
 * Creating an outlet: `POST /v1/outlets`, four fields.
 *
 * **The 409 belongs to the code field.** `IDENTITY_OUTLET_CODE_TAKEN` is a
 * statement about one input, and the difference between rendering it next to
 * that input and rendering it above the form is the difference between a person
 * correcting four characters and a person re-reading four fields to work out
 * which one the server meant. `docs/ui-conventions.md` asks for errors beside
 * the field they concern; this is the case that makes the rule worth having.
 *
 * Everything else the API can refuse with goes above the submit, because
 * nothing else here is about one field — a lapsed session, a role that does not
 * permit this, a network that went away.
 *
 * **The copy is never written here.** `messageForApiError` turns the code into
 * a sentence and it is the only thing that does; the code is what is branched
 * on, never the message and never the status. A 409 from a proxy is not this
 * refusal.
 *
 * A dialog rather than a route: creating an outlet is four fields and the list
 * behind it is the context for the codes already in use. The primary action
 * inside a dialog is the dialog's, not a second primary on the screen — the
 * screen's is the button that opened this.
 */

const NAME_REQUIRED = 'Enter a name for this outlet.';

/**
 * What this dialog will send, and what it refuses before sending.
 *
 * The code rule is imported rather than restated: `EditOutletDialog` does not
 * offer a code and this is the only form that sets one today, but the rule is
 * about outlet codes rather than about this dialog, and the copy of it that
 * lived here was the shape that let `PIN_MALFORMED` end up written twice.
 *
 * `kind` is read off the API's own union rather than restated, so a fifth kind
 * added to the document fails to compile here.
 */
const newOutletSchema = z.object({
  code: outletCodeSchema,
  name: z.string().trim().min(1, NAME_REQUIRED),
  kind: z.custom<OutletKind>(),
  carriesStock: z.boolean(),
  // Required with no default (#321): a guessed registration prints the wrong
  // GSTIN on every invoice the outlet issues, which is the fault #534 fixed.
  taxIdentityId: z.string().min(1, TAX_IDENTITY_REQUIRED),
});

type NewOutletValues = z.infer<typeof newOutletSchema>;

export function NewOutletDialog({
  open,
  onOpenChange,
  onCreated,
  taxIdentities,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (outlet: OutletResource) => void;
  taxIdentities: readonly TaxIdentityResource[];
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <FormDialogContent>
        <DialogHeader>
          <DialogTitle>New outlet</DialogTitle>
          <DialogDescription>
            A place in the park that takes money. The code is what appears on screens and receipts,
            and it cannot be changed afterwards.
          </DialogDescription>
        </DialogHeader>

        {/* The form is a child so that closing the dialog unmounts it and the
            next open starts empty. Holding the fields in this component would
            leave a half-typed outlet — and a refusal about it — waiting the next
            time somebody opened the form. */}
        <NewOutletForm onCreated={onCreated} taxIdentities={taxIdentities} />
      </FormDialogContent>
    </Dialog>
  );
}

function NewOutletForm({
  onCreated,
  taxIdentities,
}: {
  onCreated: (outlet: OutletResource) => void;
  taxIdentities: readonly TaxIdentityResource[];
}) {
  const client = useApiClient();

  const form = useForm({
    resolver: zodResolver(newOutletSchema),
    mode: 'onTouched',
    defaultValues: {
      code: '',
      name: '',
      kind: 'GATE' as OutletKind,
      carriesStock: true,
      taxIdentityId: '',
    },
  });

  const [formError, setFormError] = useState<string | null>(null);

  // One key for as long as this form is open, which is exactly one attempt at
  // creating one outlet: the dialog unmounts on success, so the next outlet gets
  // a fresh one. A refusal rolls the claim back server-side — only
  // `BUSINESS_DAY_CLOSED` is retained — so correcting a taken code and
  // submitting again re-claims cleanly rather than colliding with itself.
  const [idempotencyKey] = useState(newIdempotencyKey);

  const pending = form.formState.isSubmitting;

  // Whether somebody has moved the stock switch themselves. Once they have,
  // changing the kind stops overwriting their answer — `carriesStock` is a
  // field of its own on the API's DTO precisely because an outlet may not
  // follow its kind, and a form that silently re-decides it would make that
  // impossible to express.
  const stockDecided = useRef(false);

  // Watched rather than read from a second copy in state: the hint under the
  // picker has to follow the value the resolver will actually be given.
  const kind = form.watch('kind');

  const changeKind = (next: OutletKind) => {
    form.setValue('kind', next);

    if (!stockDecided.current) form.setValue('carriesStock', ordinarilyCarriesStock(next));
  };

  const handleSubmit = async ({
    code,
    name,
    kind: chosen,
    carriesStock,
    taxIdentityId,
  }: NewOutletValues) => {
    // Belt and braces with the disabled button below. A second submit of the
    // same code is refused by the API's own uniqueness rule, which reads as
    // "that code is taken" moments after it was accepted.
    if (pending) return;

    setFormError(null);

    try {
      // The body's shape comes from the generated client. Nothing about
      // `POST /v1/outlets` is written down in this file. Both strings arrive
      // trimmed, because the schema trims them rather than this function.
      const body: OutletsController_create_v1Body = {
        code,
        name,
        kind: chosen,
        carriesStock,
        taxIdentityId,
      };

      onCreated(
        await client.request(OutletsController_create_v1, {
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
        }),
      );
    } catch (error) {
      if (error instanceof ApiError && error.code === 'IDENTITY_OUTLET_CODE_TAKEN') {
        // Beside the field, and the focus goes with it. Somebody who submitted
        // with the keyboard is otherwise left with the caret on the button and
        // a message they have to go and find. A 409 that names one input is not
        // a statement about the form, and rendering it above the submit is the
        // difference between correcting four characters and re-reading four
        // fields.
        form.setError('code', { message: messageForFailure(error) });
        form.setFocus('code');
      } else if (error instanceof ApiError && error.code === 'RESOURCE_NOT_FOUND') {
        // The one id this body carries is the registration's, so a 404 here
        // means it went while the form was open.
        form.setError('taxIdentityId', { message: messageForFailure(error) });
      } else {
        setFormError(messageForFailure(error));
      }

      // Nothing is cleared. The retry is one corrected field, and re-typing the
      // other three to fix it is a cruelty this form is in no position to
      // inflict.
    }
  };

  return (
    // One column, per docs/ui-conventions.md. `noValidate` because the browser's
    // own bubbles are not this app's error language and cannot be worded.
    <Form {...form}>
      <form
        className="space-y-5"
        noValidate
        onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
      >
        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                Outlet code
              </FormLabel>
              <FormControl
                render={
                  <Input
                    // A code is read aloud and typed back in: mono face,
                    // tabular figures.
                    className={`${fieldSurface} h-11 font-mono tracking-wider numeric`}
                    required
                    // Short and uppercase, which nothing on this screen said
                    // until FE019 — a person discovered it by being refused.
                    placeholder="DOLL"
                    autoComplete="off"
                    autoCapitalize="characters"
                    {...field}
                    onChange={(event) => {
                      // Uppercased as it is typed, because the API stores it
                      // uppercase. Left alone, somebody types `doll`, the list
                      // comes back saying `DOLL`, and the screen looks like it
                      // changed their answer.
                      field.onChange(event.target.value.toUpperCase());
                    }}
                  />
                }
              />
              <FormDescription>
                Short, and unique among the outlets still trading. Retiring an outlet releases its
                code for reuse.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                Name
              </FormLabel>
              <FormControl
                render={
                  <Input
                    className={`${fieldSurface} h-11`}
                    required
                    placeholder="Doll Shop"
                    autoComplete="off"
                    {...field}
                  />
                }
              />
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="kind"
          render={() => (
            <FormItem>
              <FormLabel id="outlet-kind-label" className={fieldLabel}>
                Kind
              </FormLabel>
              {/* Not wrapped in `FormControl`: the trigger is a button rather
                  than the input the generated id belongs on, and it already
                  carries its own accessible name. The picker cannot be left
                  empty — it opens on a kind — so there is nothing here for the
                  resolver to refuse. */}
              <Select
                value={kind}
                items={OUTLET_KIND_ITEMS}
                onValueChange={(next) => changeKind(next as OutletKind)}
              >
                <SelectTrigger
                  id="outlet-kind"
                  aria-label="Kind"
                  className={`${fieldSurface} h-11 w-full`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {OUTLET_KINDS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {labelForKind(option)}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              <FormDescription>{hintForKind(kind)}</FormDescription>
            </FormItem>
          )}
        />

        {/* A quiet inset panel, and the one place the contrast trap lives:
            `text-muted-foreground` on `bg-muted` measures 4.39:1 and misses AA.
            The text here is `text-foreground` for that reason and no other. */}
        <FormField
          control={form.control}
          name="carriesStock"
          render={({ field }) => (
            <div className="flex items-start gap-3 rounded-2xl bg-muted px-4 py-3">
              <Switch
                id="carries-stock"
                aria-labelledby="carries-stock-label"
                className="mt-0.5"
                checked={field.value}
                onCheckedChange={(checked) => {
                  stockDecided.current = true;
                  field.onChange(checked);
                }}
              />
              <div className="space-y-1">
                <Label
                  id="carries-stock-label"
                  htmlFor="carries-stock"
                  className="cursor-pointer text-sm text-foreground"
                >
                  Holds stock
                </Label>
                <p className="text-sm text-foreground/70">
                  Off for an activity centre, which takes money for rides but sells nothing off a
                  shelf. Suggested from the kind, and yours to change.
                </p>
              </div>
            </div>
          )}
        />

        <FormField
          control={form.control}
          name="taxIdentityId"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel required className={fieldLabel}>
                GST registration
              </FormLabel>
              {/* Not wrapped in `FormControl`, like the kind picker: the trigger
                  is a button with its own accessible name, and it is described
                  by the message by hand. */}
              <RegistrationSelect
                id="outlet-tax-identity"
                value={field.value}
                onChange={field.onChange}
                identities={taxIdentities}
                errorId={fieldState.error === undefined ? undefined : 'outlet-tax-identity-error'}
              />
              <FormDescription>
                Whose GSTIN this outlet’s tills print. Fixed once the outlet has a till.
              </FormDescription>
              <FormMessage id="outlet-tax-identity-error" />
            </FormItem>
          )}
        />

        {/* Above the submit and it stays there — a toast vanishes, and somebody
            who looked away has no way to get it back. */}
        {formError !== null && (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="submit" disabled={pending}>
            {pending ? 'Creating…' : 'Create outlet'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
